import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const app = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_BUCKET,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
});

const emulatorHost = process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST;

// When the page is served over https (e.g. the PowerPoint add-in) the browser
// blocks the plain-http emulator as mixed content, and connectFirestoreEmulator
// always uses http. Talk to the emulator through a same-origin https path that
// the Next dev server proxies to the emulator (see rewrites in next.config.js).
// Long polling is required because the WebChannel's default streaming fetch
// doesn't survive the proxy hop.
const useHttpsEmulatorProxy =
  emulatorHost &&
  typeof window !== 'undefined' &&
  window.location.protocol === 'https:';

/** @type {import('firebase/firestore').Firestore} */
let db;
if (useHttpsEmulatorProxy) {
  console.log('Using Firestore emulator via same-origin https proxy');
  db = initializeFirestore(app, {
    host: window.location.host,
    ssl: true,
    experimentalForceLongPolling: true,
  });
} else {
  // Auto-detect long polling so Firestore falls back from streaming WebChannel to
  // long polling in environments that silently stall the stream — notably the
  // PowerPoint content add-in WebView and corporate proxies/VPNs that buffer the
  // streaming response indefinitely. Streaming can wedge half-open with no error,
  // so the SDK's reconnect never fires and onSnapshot listeners freeze until the
  // context is torn down (reopen PowerPoint). Long polling fails detectably, letting
  // the SDK recover. See local-refs/webview_issues.md and firebase-js-sdk#1674.
  db = initializeFirestore(app, { experimentalAutoDetectLongPolling: true });
  if (emulatorHost) {
    console.log('Using Firestore emulator');
    connectFirestoreEmulator(db, emulatorHost, 8080);
  }
}

const realTimeDb = getDatabase(app);
const storage = getStorage();
const auth = getAuth(app);

export default db;

export { storage, realTimeDb, auth };
